import { Box, Link, Tooltip, Typography } from '@mui/material';
import { Job } from '~/state/atoms/jobAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { useJobTimer } from './useJobTimer';

const statusTextMap: Partial<Record<Job['status'], string>> = {
    Queued: 'Task queued',
    Uploading: 'Uploading files...',
    Received: 'Uploading files...',
    Processing: 'Processing...',
    Downloading: 'Downloading results...',
    RetryDownload: 'Retrying download...',
    Canceled: 'Canceled task',
};

type JobPhaseProps = {
    job: Job;
    onClear: (job: Job) => void;
    onRetryDownload: (job: Job) => void;
    anchorEl: HTMLDivElement | null;
};

export const JobPhase = ({
    job,
    onClear,
    onRetryDownload,
    anchorEl,
}: JobPhaseProps) => {
    const elapsedTime = useJobTimer(job);
    const load = async () => {
        if (job.results) {
            await job.onLoad?.(job);
            onClear(job);
        }
    };

    const renderStatus = () => {
        const textProps = {
            variant: 'body2' as const,
            fontSize: 12,
            sx: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        };

        switch (job.status) {
            case 'Done':
                return job.type === WorkflowTypes.DATA_UPLOAD ? (
                    <Typography {...textProps} data-testid="job-done-label">
                        Files uploaded successfully
                    </Typography>
                ) : job.isImported ? (
                    <Typography {...textProps} data-testid="job-done-label">
                        Files imported successfully
                    </Typography>
                ) : (
                    <Typography {...textProps} data-testid="job-done-label">
                        Task finished:{' '}
                        <Link
                            component="button"
                            fontSize={12}
                            onClick={load}
                            data-testid="load-job-results-button"
                        >
                            Load results
                        </Link>
                    </Typography>
                );
            case 'Error':
                return (
                    <Box display="flex" gap={0.5} alignItems="baseline">
                        <Tooltip
                            title={job.error || ''}
                            placement="left-start"
                            PopperProps={{
                                anchorEl: anchorEl,
                            }}
                        >
                            <Typography
                                {...textProps}
                                data-testid="job-error-label"
                            >
                                {job.downloadRetryData
                                    ? 'Download failed.'
                                    : job.error || 'Unknown error'}
                            </Typography>
                        </Tooltip>
                        {job.downloadRetryData && (
                            <Link
                                component="button"
                                fontSize={12}
                                onClick={() => onRetryDownload(job)}
                                data-testid="retry-download-button"
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                Retry download
                            </Link>
                        )}
                    </Box>
                );
            default:
                const text = statusTextMap[job.status];
                return text ? (
                    <Typography {...textProps}>{text}</Typography>
                ) : null;
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            position="relative"
            overflow="hidden"
            width="100%"
        >
            {renderStatus()}
            {elapsedTime && (
                <Typography
                    variant="body2"
                    fontSize={12}
                    sx={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
                >
                    {elapsedTime}
                </Typography>
            )}
        </Box>
    );
};
